'use client';

import {
  Button,
  ChatThread,
  ChatBubble,
  Flex,
  Avatar,
  Text,
  File,
} from '@/_components';
import { downloadAttachment } from '@/_api/client-documents';
import { DOCUMENT_ANSWER_PREFIX } from '@/_constants/documents';
import { useContext, type FC, useMemo } from 'react';
import { UserContext } from '@/_contexts/UserProvider';
import { ClientConversationsContext } from '@/_contexts/client/ClientConversationsProvider';
import { QUESTION_SEPARATOR } from '@/_constants/questions';
import type { Message } from '@/_types/models/conversations';
import type { ClientChatMessageProps } from './ClientChatMessage.types';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { processMessageText } from '@/_utils/strings';

export const ClientChatMessage: FC<ClientChatMessageProps> = ({
  message,
  isReply,
  isActive,
  onSelectReply,
  unreadThreadMessageCount,
  isMe,
}) => {
  const { setPreviewFile } = useContext(PreviewContext);

  const { selectedMessage, reloadMessages } = useContext(
    ClientConversationsContext,
  );
  const { token } = useContext(UserContext);
  const { firm } = useContext(FirmSettingsContext);

  const isAttached: boolean = message?.text?.startsWith?.(
    DOCUMENT_ANSWER_PREFIX,
  );

  const attachments: Array<string> = useMemo(() => {
    return isAttached
      ? message?.text
          .replace(DOCUMENT_ANSWER_PREFIX, '')
          .split(QUESTION_SEPARATOR)
          .filter((attachment) => attachment.trim() !== '')
      : [];
  }, [isAttached, message?.text]);

  const threadLevelMessages: Array<Message> = useMemo(
    () =>
      selectedMessage?.messages?.filter?.(
        (m: any) => m.message_thread_id === message.id,
      ) || [],
    [selectedMessage, message],
  );

  const lastReplyDate: string = useMemo(() => {
    let latest = new Date();

    if (message.created_at) latest = new Date(message.created_at);

    const hasThreads = threadLevelMessages?.length;

    if (hasThreads) {
      threadLevelMessages?.forEach((thread) => {
        const threadCreatedAt = new Date(thread.created_at);
        if (threadCreatedAt > latest) {
          latest = threadCreatedAt;
        }
      });
    }

    return latest?.toISOString?.();
  }, [threadLevelMessages, message?.created_at]);

  const onOpenThread = () => {
    onSelectReply?.(message);
    reloadMessages(true); // ensures unread message count is immediately updated
  };

  const participants: Array<string> = useMemo(() => {
    const uniqueNamesSet: Set<string> = new Set(
      threadLevelMessages?.map((message) => message.user_name),
    );
    const uniqueNamesArray: Array<string> =
      Array.from(uniqueNamesSet).filter(Boolean);

    return uniqueNamesArray;
  }, [threadLevelMessages]);

  const firmName: string | undefined = firm?.name;
  const firmTag: string | undefined = useMemo(() => {
    return firmName && message?.is_firm_user ? `${firmName} Team` : undefined;
  }, [firmName, message?.is_firm_user]);

  const messageDeleted: boolean = message?.text === 'This message was deleted';

  return (
    <ChatBubble
      id={message.id}
      date={message.created_at}
      firm={firmTag}
      unreadThreadMessageCount={unreadThreadMessageCount}
      isMe={isMe}
      isActive={isActive}
      isScheduled={!!message?.scheduled}
      isReply={isReply}
      onOpenThread={onOpenThread}
      avatarName={message.user_name}
      isEdited={message?.is_edited}
      tag={message.tag || 'general'}
    >
      {isAttached && (
        <Flex isColumn={true} gap={4}>
          <Flex isColumn={true} gap={1}>
            {attachments.map((attachment: string, key) => {
              // NOTE @chris this is to handle invalid attached in Anomaly (38)
              // https://new-internal.soraban.com/admin/T9OyliahXqn0BVn/messages/87995
              const name: string = attachment
                ? attachment?.substring(attachment?.lastIndexOf?.('/') + 1)
                : 'Unnamed';

              const onPreview = async () => {
                const { document } = await downloadAttachment({
                  link: attachment,
                  token: token!,
                });

                setPreviewFile(document?.data?.link);
              };

              return (
                <File title={name} key={key} shouldEllipse={false}>
                  <Button
                    intent="ghost"
                    onClick={onPreview}
                    size="tiny"
                    value="Preview"
                  ></Button>
                </File>
              );
            })}
          </Flex>
        </Flex>
      )}
      {messageDeleted ? (
        <Text type="small" color="secondary">
          This message was deleted
        </Text>
      ) : isAttached ? null : (
        <div
          dangerouslySetInnerHTML={{
            __html: processMessageText(message.text),
          }}
        />
      )}
      {!isActive && !isReply && threadLevelMessages?.length > 0 && (
        <ChatThread
          onClick={onOpenThread}
          lastReplyDate={lastReplyDate}
          replyCount={threadLevelMessages?.length ?? 0}
          participantCount={participants?.length}
        >
          {participants?.map((participant, key) => {
            return (
              <Avatar key={key} type="user" name={participant} size={24} />
            );
          })}
        </ChatThread>
      )}
    </ChatBubble>
  );
};
